import { useState, useEffect, useRef, useCallback } from 'react';
import { manualTradingApi } from '../../../services/api';
import { ModelStats, ModelStatsMap } from '../../../types/analytics.types';
import {
  getMLClosedTrades, computeModelStats,
  getModelStatsKey,
} from '../../../utils/tradeAnalytics';
import { getCurrentTradingDay } from '../../../utils/marketHours';
import { parseUTC } from '../../../utils/formatters';
import { MODEL_KEYS } from '../../../config/analytics';

export interface ModelTradeRecord {
  profitLoss: number;
  isWin: boolean;
  instrument: string;
  direction: string;
  entryTime: string;
  exitTime: string;
}

export interface EnrichedModelStats {
  allTime: ModelStats;
  today: ModelStats;
  lastTrades: ModelTradeRecord[];
}

export type EnrichedModelStatsMap = Record<string, EnrichedModelStats>;

export interface DaySummary {
  totalTrades: number;
  wins: number;
  losses: number;
  totalPL: number;
  bestModel: { name: string; pl: number } | null;
  worstModel: { name: string; pl: number } | null;
}

const createEmptyStats = (): ModelStats => ({
  totalTrades: 0, totalPL: 0, totalLosses: 0, wins: 0, losses: 0, winRate: 0,
  avgPerWin: 0, avgPerLoss: 0, profitFactor: 0, largestWin: 0, largestLoss: 0,
  totalWinAmount: 0, totalLossAmount: 0, avgReturnPerTrade: 0, expectancy: 0,
  riskRewardRatio: 0, totalWinHoldingTime: 0, totalLossHoldingTime: 0,
  totalHoldingTime: 0, avgHoldingTimeWins: 0, avgHoldingTimeLosses: 0, avgHoldingTimeAll: 0,
  avgReturnPerUnit: 0, avgWinPerUnit: 0, avgLossPerUnit: 0,
});

export function useModelStats() {
  const [statsMap, setStatsMap] = useState<EnrichedModelStatsMap>({});
  const [daySummary, setDaySummary] = useState<DaySummary | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAndCompute = useCallback(async () => {
    try {
      const allTrades = await manualTradingApi.getTrades();
      const closedML = getMLClosedTrades(allTrades);

      // All-time stats
      const allTimeStats = computeModelStats(closedML);

      // Today's stats (trading day: 4 PM UTC-6 → 3 PM UTC-6)
      const { start: dayStart, end: dayEnd } = getCurrentTradingDay();
      const todayTrades = closedML.filter((t: any) => {
        const entryDate = parseUTC(t.entry_time);
        return entryDate >= dayStart && entryDate <= dayEnd;
      });
      const todayStats = computeModelStats(todayTrades);

      // Group closed trades by model for "last 3 trades" lookup
      const tradesByModel: Record<string, any[]> = {};
      for (const trade of closedML) {
        const key = getModelStatsKey(trade);
        if (!key) continue;
        if (!tradesByModel[key]) tradesByModel[key] = [];
        tradesByModel[key].push(trade);
      }

      // Build enriched map
      const enriched: EnrichedModelStatsMap = {};
      for (const modelKey of MODEL_KEYS) {
        const lastTrades = (tradesByModel[modelKey] || [])
          .sort((a: any, b: any) =>
            parseUTC(b.exit_time || b.entry_time).getTime() -
            parseUTC(a.exit_time || a.entry_time).getTime()
          )
          .slice(0, 3)
          .map((t: any) => ({
            profitLoss: t.profit_loss || 0,
            isWin: (t.profit_loss || 0) > 0,
            instrument: t.instrument || '',
            direction: t.direction || '',
            entryTime: t.entry_time || '',
            exitTime: t.exit_time || '',
          }));

        enriched[modelKey] = {
          allTime: allTimeStats[modelKey] || createEmptyStats(),
          today: todayStats[modelKey] || createEmptyStats(),
          lastTrades,
        };
      }

      setStatsMap(enriched);

      // Day summary for the boss
      let dayTotalPL = 0, dayWins = 0, dayLosses = 0, dayTotal = 0;
      let bestModel: { name: string; pl: number } | null = null;
      let worstModel: { name: string; pl: number } | null = null;

      for (const key of MODEL_KEYS) {
        const stat = todayStats[key];
        if (!stat || stat.totalTrades === 0) continue;
        dayTotal += stat.totalTrades;
        dayTotalPL += stat.totalPL;
        dayWins += stat.wins;
        dayLosses += stat.losses;
        if (!bestModel || stat.totalPL > bestModel.pl) {
          bestModel = { name: key, pl: stat.totalPL };
        }
        if (!worstModel || stat.totalPL < worstModel.pl) {
          worstModel = { name: key, pl: stat.totalPL };
        }
      }

      setDaySummary({
        totalTrades: dayTotal,
        wins: dayWins,
        losses: dayLosses,
        totalPL: dayTotalPL,
        bestModel,
        worstModel,
      });
    } catch (err) {
      console.error('[useModelStats] Failed to fetch trades:', err);
    }
  }, []);

  useEffect(() => {
    fetchAndCompute();
    intervalRef.current = setInterval(fetchAndCompute, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchAndCompute]);

  return { statsMap, daySummary };
}
